import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { publishEvent, subscribeEvent, unsubscribeEvent } from "core/eventsConnector";

import BoxFilter from "./BoxFilters";
import ButtonsActions from "../utils/buttonsActions";
import TopBarActions from "./TopBarActions";
import { SwalAlertChoice, SwalAlertClass } from "core/swalClass";
import NavBreadcrum from "components/NavBreadcrum";
import TableNotifications from "./TableNotifications";
import Message from "./Message";
import LoaderSpinner from "context/LoaderSpinner";
import { MessageModel } from "../../site-apps/notifications/MessageModel";

import axiApi from "ApiCaller";
import navs from "./navs";
import ItemsModel from "core/itemsModel";
import FilterModel from "components/FilterModel";
import TopBarPagination from "components/paginator/TopBarPagination-v2";
import { PagerModel } from "components/paginator/PaginationModel";

const ViewNotifications = () => {
    const { t } = useTranslation("routes");
    const { t: _Tf } = useTranslation("forms");
    const bread = [{ name: t("labels.notifications"), current: true }];
    const title = t("labels.notifications");
    document.title = title;
    const items = useRef(new ItemsModel());
    const [isLoading, setIsLoading] = useState(false);
    const [itemsShown, setItemsShown] = useState([]);
    const [showPrev, setShowPrev] = useState(false);
    const pagerModelRef = useRef(new PagerModel());
    const [item, setItem] = useState({ ...MessageModel });
    const [checkAll, setCheckAll] = useState(false);
    const Alert = new SwalAlertClass({ confirmButtonText: "Aceptar" });
    const AlertQuestion = new SwalAlertChoice({
        confirmButtonText: "Aceptar",
        cancelButtonText: "Cancelar",
    });

    const [buttonsTB, setButtonsTB] = useState({
        ...new ButtonsActions(),
        title: document.title,
    });

    const [isSelected, setIsSelected] = useState(false);

    const filters = useRef(new FilterModel());

    const userLang = localStorage.getItem("i18nextLng") || "es";

    useEffect(() => {
        const buttons = { ...buttonsTB };
        buttons.title = title;
        buttons.delete.active = true;
        buttons.delete.onClick = () => onClickTrash();
        buttons.delete.disabled = true;
        buttons.filter.active = true;
        buttons.filter.onClick = onClickFilter;
        buttons.filter.disabled = items.current.showed.filter((item) => !item.read).length === 0;
        buttons.markAllAsRead.active = true;
        buttons.markAllAsRead.onClick = markAllAsRead;
        buttons.markAllAsRead.disabled = items.current.showed.filter((item) => !item.read).length === 0;
        setButtonsTB(buttons);
        pagerModelRef.current.handleCallApi = () => fetchNotifications();
        items.current.setItem = setItem;
        items.current.setShowPrev = setShowPrev;
        items.current.item = MessageModel;
        fetchGetNotifications();
        subscribeEvent("messageRead", onMessageRead);
        return () => {
            unsubscribeEvent("messageRead", onMessageRead);
        };
    }, []);

    useEffect(() => {
        const buttons = { ...buttonsTB };
        buttons.delete.disabled = !items.current.chosen.length;
        const unreadCount = itemsShown.filter((item) => !item.read).length;
        if (buttons.markAllAsRead) {
            buttons.markAllAsRead = {
                ...buttons.markAllAsRead,
                active: true,
                disabled: unreadCount === 0,
            };
        }
        // El botón de filtro no se desactiva nunca aquí
        setButtonsTB(buttons);
    }, [itemsShown, items.current.chosen]);

    filters.current.filter = () => {
        pagerModelRef.current.paging.page = 1;
        fetchNotifications();
    };

    const fetchGetNotifications = async () => {
        setIsLoading(true);
        axiApi
            .get(`/api/notify/get/`)
            .then((resp) => {
                if (resp.data.items) {
                    items.current.all = [...resp.data.items];
                    items.current.showed = [...resp.data.items];
                    setItemsShown([...resp.data.items]);

                    pagerModelRef.current.active = resp.data.items.length > 0;
                    pagerModelRef.current.paging.setPager(resp.data);
                }
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const fetchNotifications = async () => {
        setIsLoading(true);
        try {
            const resp = await axiApi.post("/api/notify/get/", filters.current.chosen, {
                params: pagerModelRef.current.paging.getParams(),
            });
            if (resp.data.items) {
                items.current.all = resp.data.items;
                items.current.showed = resp.data.items;
                setItemsShown([...resp.data.items]);
                pagerModelRef.current.active = resp.data.items.length > 0;
                pagerModelRef.current.paging.setPager(resp.data);
            } else {
                items.current.showed = [];
                setItemsShown([]);
            }
        } catch (error) {
            console.error("Error al obtener notificaciones:", error);
        } finally {
            setIsLoading(false);
        }
    };

    function onClickMessage(item) {
        setItem(item);
        setIsSelected(true);
        setButtonsTB((prev) => ({
            ...prev,
            delete: {
                ...prev.delete,
                disabled: false,
            },
        }));
        if (!item.read) {
            markAsRead(item);
        }
    }

    function onClickFilter() {
        let buttons = { ...buttonsTB };
        buttons.title = t("labels.notifications");
        buttons.filter.click = !buttonsTB.filter.click;
        setButtonsTB(buttons);
    }

    function markAsRead(message) {
        axiApi
            .post(`/api/notify/read/`, { id: message.id })
            .then((resp) => {
                if (buttonsTB.filter.click) {
                    let buttons = { ...buttonsTB };
                    buttons.filter.click = false;
                    setButtonsTB(buttons);
                }
                const updated = { ...message, read: true };
                items.current.showed = items.current.showed.map((item) => (item.id === updated.id ? updated : item));
                setItemsShown([...items.current.showed]);
                if (typeof onMessageRead === "function") {
                    onMessageRead({ detail: { id: updated.id } });
                }
                publishEvent("messageRead", { id: updated.id });
            })
            .catch((error) => {
                console.error("markAsRead -> error", error);
            });
    }

    function markAllAsRead() {
        AlertQuestion.warning(_Tf("labels.read")).then((result) => {
            if (result.isConfirmed) {
                const ids = items.current.showed.map((item) => item.id);
                axiApi
                    .post(`/api/notify/all/read/`, { ids })
                    .then((resp) => {
                        const updatedItems = items.current.showed.map((item) => ({ ...item, read: true }));
                        items.current.showed = updatedItems;
                        items.current.all = items.current.all.map((item) => ({ ...item, read: true }));
                        items.current.chosen = [];
                        setItemsShown(updatedItems);
                        setIsSelected(false);
                        setButtonsTB((prev) => {
                            const updated = { ...prev };
                            if (updated.markAllAsRead) {
                                updated.markAllAsRead.disabled = true;
                            }
                            return updated;
                        });
                        publishEvent("messageRead", { ids });
                    })
                    .catch((error) => {
                        console.error("markAllAsRead -> error", error);
                    });
            }
        });
    }

    function onClickTrash() {
        AlertQuestion.warning(_Tf("labels.warning"), _Tf("delete.move_to_trash")).then((result) => {
            if (result.isConfirmed) {
                axiApi
                    .post(`/api/notify/trash/`, { ids: items.current.getChosenIds() })
                    .then((resp) => {
                        if (resp.data.status) {
                            fetchGetNotifications();
                            setIsSelected(false);
                            items.current.clearChosen();
                            setCheckAll(false);
                            setButtonsTB((prev) => ({
                                ...prev,
                                delete: {
                                    ...prev.delete,
                                    disabled: true,
                                },
                            }));
                            Alert.success(_Tf("labels.success"));
                            publishEvent("messageTrashed", { ids: items.current.getChosenIds() });
                        }
                    })
                    .catch((error) => {
                        console.error("onClickTrash -> error", error);
                    });
            }
        });
    }

    const onMessageRead = (event) => {
        let ids = [];
        if (event.detail) {
            if (event.detail.id) ids = [event.detail.id];
            else if (Array.isArray(event.detail.ids)) ids = event.detail.ids;
        }

        items.current.showed = items.current.showed.map((item) => (ids.includes(item.id) ? { ...item, read: true } : item));
    };


    return (
        <>
            <NavBreadcrum navs={navs} bread={bread} />
            <TopBarActions actions={buttonsTB} title={title} />
            <BoxFilter show={buttonsTB.filter.click} filtersRef={filters} />
            <TopBarPagination pagerModel={pagerModelRef.current} stickyTopValue="125px" chosen={items.current.chosen} />
            <LoaderSpinner isLoading={isLoading}>
                <div className="row" style={buttonsTB.filter.click ? { marginTop: "20px" } : {}}>
                    <div className="col-md-4 col-12" style={{ overflowY: "auto", height: "calc(100vh - 130px)" }}>
                        <TableNotifications
                            itemsShown={itemsShown}
                            onClickRow={onClickMessage}
                            isSelected={isSelected}
                            userLang={userLang}
                            buttonsTB={buttonsTB}
                            setButtonsTB={setButtonsTB}
                            checkAll={checkAll}
                            setCheckAll={setCheckAll}
                            items={items}
                        />
                    </div>
                    <div className="col-md-8 col-12 prev-item">
                        <Message item={item} userLang={userLang} />
                    </div>
                    <label className={itemsShown.length === 0 ? "" : "d-none"}>No hay resultados que mostrar</label>
                </div>
            </LoaderSpinner>
        </>
    );
};

export default ViewNotifications;